import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import { useModifiers } from "@/features/react-editor/hooks/useModifiers";
import { useReactRenderStore } from "@/features/react-editor/store/reactRenderStore";
import { findPageFileFromPath } from "@/features/react-editor/utils/filesUtils";
import Button from "@replo/design-system/components/button/Button";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { skipToken } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useParams } from "react-router-dom";

const Sections = [
  { name: "FaqOne", path: "../templates/sections/faq/faq-one" },
  { name: "FooterOne", path: "../templates/sections/footer/footer-one" },
  { name: "HeroOne", path: "../templates/sections/hero/hero-one" },
  { name: "PricingFour", path: "../templates/sections/pricing/pricing-four" },
];

export default function Modifiers() {
  const {
    selectedElement,
    setSelectedElement,
    setSectionClicked,
    classNames,
    setClassNames,
    sectionClicked,
  } = useReactRenderStore();

  const projectId = useCurrentProjectId();
  const { pageId } = useParams();

  // NOTE (Bobby, 2025-06-26): Modifiers is not currently used since the webcontainer and this
  // file tree should come from nextJSProject service instead. Kurt is doing the work on
  // modifiers and will be pulling files from nextJSProject service.
  // const { data: files = [], refetch: refetchFiles } = trpc.file.list.useQuery(
  //   projectId ? { projectId } : skipToken,
  // );

  const { data: page, isLoading: isLoadingPage } =
    trpc.nextJSProject.getPageComponent.useQuery(
      projectId ? { projectId, id: pageId ?? "" } : skipToken,
      {
        enabled: Boolean(projectId && pageId),
      },
    );

  const { mutate: addSection, isPending: isAddingSection } =
    trpc.modifiers.addSection.useMutation({
      onSuccess: () => {
        // void refetchFiles();
        setSelectedElement(null);
        setSectionClicked(false);
      },
    });

  const { applyChange, isLoading: isPending } = useModifiers();

  React.useEffect(() => {
    if (selectedElement?.props.className) {
      setClassNames(selectedElement.props.className);
    } else if (!selectedElement) {
      setClassNames("");
    }
  }, [selectedElement, setClassNames]);

  const handleSectionClick = (section: { name: string; path: string }) => {
    if (selectedElement && page && projectId) {
      const openFile = findPageFileFromPath(page.pageRoute ?? "", []);
      if (
        openFile &&
        selectedElement.file &&
        selectedElement.line !== null &&
        selectedElement.column !== null &&
        selectedElement.childText !== null
      ) {
        addSection({
          projectId,
          openFile,
          selectedElement: {
            props: selectedElement.props,
            file: selectedElement.file,
            line: selectedElement.line,
            column: selectedElement.column,
            childText: selectedElement.childText,
          },
          sectionName: section.name,
          sectionPath: section.path,
        });
      }
    }
  };

  const handleSave = () => {
    if (!selectedElement) {
      return;
    }

    const originalClassName = selectedElement.props.className ?? "";
    const hasClassNameChanged = classNames !== originalClassName;

    if (hasClassNameChanged) {
      applyChange({
        updatedClassName: classNames,
      })
        .then(() => {
          // NOTE (Kurt, 2025-07-03): This is not ideal, setting the selected element to null takes the user
          // out of the modifier state in the chat, so its really jarring; however, we need to do this so that
          // the TipTap overlay closes. We can try to rerender the overlay with the updated element, but I haven't
          // had any luck getting that approach to work. This flow will somewhat get rewritten when I implement
          // building blocks for modifiers. So leaving this in for now.
          setSelectedElement(null);
          setSectionClicked(false);
        })
        .catch((error) => {
          console.error("Failed to save modifier changes:", error);
          // Error handling is already done by the hook via TRPC error handling
        });
    }
  };

  const canSave =
    selectedElement &&
    page &&
    classNames !== (selectedElement.props.className ?? "");

  return (
    <div className="relative flex p-3 flex-col gap-4 flex-start border border-border rounded-xl bg-white">
      <X
        className="absolute top-3 right-3 w-[14px] h-[14px] cursor-pointer"
        onClick={() => {
          setSelectedElement(null);
          setSectionClicked(false);
        }}
      />
      <div className="flex items-center font-semibold text-sm">
        Selected element
      </div>
      <div>
        <span className="text-sm font-medium text-muted">Classname</span>
        <Textarea
          value={classNames}
          onChange={(value) => setClassNames(value)}
          isDisabled={isLoadingPage}
        />
      </div>
      {sectionClicked && (
        <div>
          <span className="text-sm font-medium text-muted">Add Section</span>
          {isAddingSection ? (
            <div className="border border-border rounded-lg p-3 bg-surface flex items-center justify-center">
              <div className="text-sm text-muted">Adding section...</div>
            </div>
          ) : (
            <div className="border border-border rounded-lg p-3 bg-surface">
              <div className="grid grid-cols-2 gap-2">
                {Sections.map((section) => (
                  <button
                    key={section.name}
                    onClick={() => handleSectionClick(section)}
                    className="p-2 text-sm border border-border rounded hover:bg-surface transition-colors"
                  >
                    {section.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          isLoading={isPending}
          disabled={!canSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
